import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { sharedStyles, getStatusColor, formatCurrency } from '../styles/sharedStyles';
import { formatDateTimeForDisplay } from '../utils/timezone';
import { formatMachineOptionLabel, formatMachineOptionLabelNoFinance } from '../utils/ticketLineAmount';
import { hideFinancialUi } from '../utils/foremanUi';
import ImageUpload from './ImageUpload';
import ImageGallery from './ImageGallery';

const ViewEditTicketModal = ({ ticket, onClose, onUpdated, edit }) => {
  const api = useApi();
  const { user } = useUser();
  const hideMoney = hideFinancialUi(user?.role);
  const [editMode, setEditMode] = useState(!!edit);
  const [formData, setFormData] = useState({
    customerId: '',
    jobId: '',
    workDate: '',
    workStartDate: '',
    workEndDate: '',
    description: '',
    notes: '',
    status: 'Pending',
    ticketItems: [
      { machineId: '', rateType: 'hourly', hoursUsed: '', loadsUsed: '', gallonsUsed: '', description: '', notes: '' }
    ]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [machines, setMachines] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [images, setImages] = useState([]);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'images', or 'emailLogs'
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingEmailLogs, setLoadingEmailLogs] = useState(false);
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [loadingEmailPreview, setLoadingEmailPreview] = useState(false);

  // Store full ticket data for email functionality
  const [fullTicketData, setFullTicketData] = useState(null);

  const [jobBudgetSummary, setJobBudgetSummary] = useState(null);
  const [jobBudgetLoading, setJobBudgetLoading] = useState(false);

  useEffect(() => {
    const loadTicketDetails = async () => {
      if (ticket?.id) {
        // If ticket doesn't have Customer.email, fetch full ticket details
        if (!ticket.Customer?.email) {
          try {
            const response = await api({ method: 'get', url: `/api/tickets/${ticket.id}` });
            if (response.data.success) {
              const fullTicket = response.data.data;
              console.log('Full ticket fetched - Customer email:', fullTicket.Customer?.email);
              setFullTicketData(fullTicket);
              setFormData({
                ...fullTicket,
                ticketItems: fullTicket.TicketItems ? fullTicket.TicketItems.map(item => ({
                  ...item,
                  employeeId: item.employeeId || '',
                  rateType: item.rateType || 'hourly',
                  hoursUsed: item.rateType === 'hourly' ? item.hoursUsed : '',
                  loadsUsed: item.rateType === 'load' ? item.loadsUsed : '',
                })) : []
              });
              setImages(fullTicket.TicketImages || []);
              return;
            }
          } catch (err) {
            console.error('Error fetching ticket details:', err);
            // Fallback: Try to fetch customer email directly if we have customerId
            if (ticket.customerId) {
              try {
                console.log('Fetching customer email directly for customerId:', ticket.customerId);
                const customerResponse = await api({ method: 'get', url: `/api/customers/${ticket.customerId}` });
                if (customerResponse.data.success && customerResponse.data.data.email) {
                  const customerEmail = customerResponse.data.data.email;
                  console.log('Customer email fetched directly:', customerEmail);
                  // Update ticket with customer email
                  const ticketWithEmail = {
                    ...ticket,
                    Customer: {
                      ...ticket.Customer,
                      email: customerEmail
                    }
                  };
                  setFullTicketData(ticketWithEmail);
                }
              } catch (customerErr) {
                console.error('Error fetching customer email:', customerErr);
              }
            }
          }
        }
        
        // Use existing ticket data
        setFullTicketData(ticket);
        setFormData({
          ...ticket,
          ticketItems: ticket.TicketItems ? ticket.TicketItems.map(item => ({
            ...item,
            employeeId: item.employeeId || '',
            rateType: item.rateType || 'hourly',
            hoursUsed: item.rateType === 'hourly' ? item.hoursUsed : '',
            loadsUsed: item.rateType === 'load' ? item.loadsUsed : '',
            gallonsUsed: item.gallonsUsed || '',
        })) : []
        });
        setImages(ticket.TicketImages || []);
      }
    };
    loadTicketDetails();
  }, [ticket, api]);

  // Fetch job budget summary when job selection changes
  useEffect(() => {
    const jobId = formData.jobId;
    if (!jobId) {
      setJobBudgetSummary(null);
      return;
    }

    if (user?.role === 'foreman') {
      setJobBudgetSummary(null);
      setJobBudgetLoading(false);
      return;
    }

    let cancelled = false;
    const fetchSummary = async () => {
      setJobBudgetLoading(true);
      try {
        const response = await api({ method: 'get', url: `/jobs/${jobId}/budget-summary` });
        if (response.data?.success) {
          if (!cancelled) setJobBudgetSummary(response.data.data);
        } else {
          if (!cancelled) setJobBudgetSummary(null);
        }
      } catch (err) {
        if (!cancelled) setJobBudgetSummary(null);
      } finally {
        if (!cancelled) setJobBudgetLoading(false);
      }
    };

    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [formData.jobId, api, user?.role]);

  useEffect(() => {
    // Load dropdown data
    const loadDropdownData = async () => {
      try {
        const [customersRes, jobsRes, employeesRes, machinesRes] = await Promise.all([
          api({ method: 'get', url: '/customers?limit=1000' }),
          api({ method: 'get', url: '/jobs?limit=1000' }),
          api({ method: 'get', url: '/employees?limit=1000' }),
          api({ method: 'get', url: '/machines?limit=1000' })
        ]);
        if (customersRes.data.success) setCustomers(customersRes.data.data.customers);
        if (jobsRes.data.success) setJobs(jobsRes.data.data.jobs);
        if (employeesRes.data.success) setEmployees(employeesRes.data.data.employees);
        if (machinesRes.data.success) setMachines(machinesRes.data.data.machines);
      } catch (err) {
        // ignore
      }
    };
    loadDropdownData();
  }, [api]);

  useEffect(() => {
    if (edit) setEditMode(true);
  }, [edit]);

  // Fetch email logs when email logs tab is active
  useEffect(() => {
    const fetchEmailLogs = async () => {
      if (activeTab === 'emailLogs' && ticket?.id) {
        setLoadingEmailLogs(true);
        try {
          const response = await api({ method: 'get', url: `/api/tickets/${ticket.id}/email-logs` });
          if (response.data.success) {
            setEmailLogs(response.data.data.emailLogs || []);
          }
        } catch (err) {
          console.error('Error fetching email logs:', err);
          setEmailLogs([]);
        } finally {
          setLoadingEmailLogs(false);
        }
      }
    };
    fetchEmailLogs();
  }, [activeTab, ticket, api]);

  // Function to refresh email logs
  const refreshEmailLogs = async () => {
    if (ticket?.id) {
      try {
        const response = await api({ method: 'get', url: `/api/tickets/${ticket.id}/email-logs` });
          if (response.data.success) {
            setEmailLogs(response.data.data.emailLogs || []);
          }
        } catch (err) {
        console.error('Error fetching email logs:', err);
      }
    }
  };

  // Function to show email preview modal
  const handleShowEmailPreview = async () => {
    const ticketId = ticket?.id;
    if (!ticketId) {
      console.error('Cannot preview email: No ticket ID');
      setError('Ticket ID is missing');
      return;
    }

    setLoadingEmailPreview(true);
    setError('');

    try {
      const response = await api({
        method: 'get',
        url: `/api/tickets/${ticketId}/email-preview`
      });

      if (response.data.success) {
        setEmailPreview(response.data.data);
        setShowEmailPreviewModal(true);
      } else {
        const errorMsg = response.data.message || 'Failed to load email preview';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error loading email preview:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to load email preview';
      setError(errorMessage);
    } finally {
      setLoadingEmailPreview(false);
    }
  };

  // Function to send ticket email (called after confirmation)
  const handleSendEmail = async () => {
    const ticketId = ticket?.id;
    if (!ticketId) {
      console.error('Cannot send email: No ticket ID');
      setError('Ticket ID is missing');
      return;
    }

    console.log('Sending email for ticket:', ticketId);
    setSendingEmail(true);
    setError('');
    setSuccess('');
    setShowEmailPreviewModal(false);

    try {
      console.log('Calling API: POST /api/tickets/' + ticketId + '/send-email');
      const response = await api({
        method: 'post',
        url: `/api/tickets/${ticketId}/send-email`
      });

      console.log('Email API response:', response.data);

      if (response.data.success) {
        setSuccess('Ticket email sent successfully!');
        // Refresh email logs if we're on the email logs tab
        if (activeTab === 'emailLogs') {
          await refreshEmailLogs();
        }
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorMsg = response.data.message || 'Failed to send email';
        console.error('Email send failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error sending email:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to send email';
      setError(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  // Check if customer email is available (use fullTicketData if available, otherwise ticket)
  const ticketForEmail = fullTicketData || ticket;
  const customerEmail = ticketForEmail?.Customer?.email;
  const hasCustomerEmail = customerEmail && typeof customerEmail === 'string' && customerEmail.trim() !== '';
  
  // Debug logging
  useEffect(() => {
    if (ticket?.id) {
      console.log('Ticket data for email check:', {
        ticketId: ticket.id,
        hasFullTicketData: !!fullTicketData,
        customerEmail: customerEmail,
        hasCustomerEmail: hasCustomerEmail,
        ticketCustomer: ticket?.Customer,
        fullTicketCustomer: fullTicketData?.Customer
      });
    }
  }, [ticket, fullTicketData, customerEmail, hasCustomerEmail]);

  const handleEdit = () => setEditMode(true);
  const handleCancelEdit = () => setEditMode(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTicketItemChange = (index, field, value) => {
    setFormData(prev => {
      const items = [...prev.ticketItems];
      items[index] = { ...items[index], [field]: value };
      if (field === 'rateType') {
        if (value !== 'hourly') items[index].hoursUsed = '';
        if (value !== 'load') items[index].loadsUsed = '';
      }
      return { ...prev, ticketItems: items };
    });
  };

  const addTicketItem = () => {
    setFormData(prev => ({
      ...prev,
      ticketItems: [
        ...prev.ticketItems,
        { machineId: '', employeeId: '', rateType: 'hourly', hoursUsed: '', loadsUsed: '', gallonsUsed: '', description: '', notes: '' }
      ]
    }));
  };

  const removeTicketItem = (index) => {
    setFormData(prev => ({
      ...prev,
      ticketItems: prev.ticketItems.length > 1 ? prev.ticketItems.filter((_, i) => i !== index) : prev.ticketItems
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    // Validate
    if (!formData.customerId || !formData.jobId || !formData.workDate) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }
    const validItems = formData.ticketItems.filter(item =>
      item.machineId &&
      item.employeeId &&
      (item.rateType === 'daily' ||
        (item.rateType === 'load' && item.loadsUsed) ||
        (item.rateType === 'hourly' && item.hoursUsed))
    );
    if (validItems.length === 0) {
      setError('Please add at least one machine with the required rate details.');
      setLoading(false);
      return;
    }
    const cleanedItems = validItems.map(item => ({
      machineId: item.machineId,
      employeeId: item.employeeId,
      rateType: item.rateType,
      hoursUsed: item.rateType === 'hourly' ? item.hoursUsed : null,
      loadsUsed: item.rateType === 'load' ? item.loadsUsed : null,
      gallonsUsed: item.gallonsUsed || 0,
      description: item.description,
      notes: item.notes
    }));
    try {
      await api({
        method: 'put',
        url: `/api/tickets/${ticket.id}`,
        data: {
          ...formData,
          status: formData.status || ticket.status || 'Pending',
          ticketItems: cleanedItems
        }
      });
      setEditMode(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating ticket');
    } finally {
      setLoading(false);
    }
  };

  // Image management functions
  const handleImagesUploaded = (newImages) => {
    setImages(prev => [...newImages, ...prev]);
  };

  const handleImageDeleted = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleImageUpdated = (updatedImage) => {
    setImages(prev => prev.map(img => img.id === updatedImage.id ? updatedImage : img));
  };

  if (!formData) return null;

  const statusColor = getStatusColor(ticket.status);

  return (
    <>
      <div className="modal-backdrop fade show" style={{ 
        zIndex: 1400,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }} onClick={onClose}></div>
      <div className="modal fade show" style={{ 
        display: 'flex', 
        zIndex: 1500, 
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '1rem'
      }} tabIndex="-1">
        {/* Responsive modal dialog - full width on mobile, centered on larger screens */}
        <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-lg" 
             style={{ 
               maxWidth: '800px', 
               width: '100%',
               margin: '0 auto',
               maxHeight: '80vh'
             }}>
          <div className="modal-content" style={{ 
            borderRadius: '16px', 
            border: 'none', 
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', 
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div className="modal-header" style={{ 
              borderBottom: '1px solid #e5e9f0', 
              padding: '1rem 1.5rem',
              flexShrink: 0
            }}>
              <div className="d-flex align-items-center flex-grow-1 flex-wrap">
                <div className="me-3">
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: sharedStyles.colors.primary.light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: sharedStyles.colors.primary.main
                  }}>
                    <i className="bi bi-receipt" style={{ fontSize: '1.25rem' }}></i>
                  </div>
                </div>
                <div className="flex-grow-1 min-w-0">
                  <h5 className="modal-title mb-1" style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: '700', 
                    color: '#1a1f36',
                    margin: 0,
                    wordBreak: 'break-word'
                  }}>
                    {editMode ? 'Edit Ticket' : 'View Ticket'}
                  </h5>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span style={{ fontSize: '0.875rem', color: '#697386' }}>
                      {ticket.ticketNumber}
                    </span>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      backgroundColor: statusColor.bg,
                      color: statusColor.text,
                      whiteSpace: 'nowrap'
                    }}>
                      {ticket.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {!editMode && (
                  <button
                    type="button"
                    className="btn"
                    onClick={handleShowEmailPreview}
                    disabled={loadingEmailPreview || sendingEmail || !hasCustomerEmail}
                    style={{
                      backgroundColor: hasCustomerEmail ? '#911311' : '#9ca3af',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.5rem 1rem',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: hasCustomerEmail && !loadingEmailPreview && !sendingEmail ? 'pointer' : 'not-allowed',
                      opacity: (loadingEmailPreview || sendingEmail || !hasCustomerEmail) ? 0.6 : 1
                    }}
                    title={!hasCustomerEmail ? `Customer email not configured. ${customerEmail ? `Found: ${customerEmail}` : 'Email field is empty or missing'}` : 'Preview and send ticket email to customer'}
                  >
                    {loadingEmailPreview ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span>Loading...</span>
                      </>
                    ) : sendingEmail ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-envelope-send"></i>
                        <span className="d-none d-md-inline">Send Email</span>
                      </>
                    )}
                  </button>
                )}
                <button type="button" className="btn-close" onClick={onClose} style={{ fontSize: '1.25rem' }}></button>
              </div>
            </div>
            
            {/* Navigation Tabs */}
            <div className="border-bottom" style={{ borderColor: '#e5e9f0', flexShrink: 0 }}>
              <ul className="nav nav-tabs border-0" role="tablist" style={{ padding: '0 1rem' }}>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link border-0 ${activeTab === 'details' ? 'active' : ''}`}
                    onClick={() => setActiveTab('details')}
                    type="button"
                    style={{
                      padding: '0.75rem 1rem',
                      borderBottom: activeTab === 'details' ? '2px solid #911311' : '2px solid transparent',
                      color: activeTab === 'details' ? '#911311' : '#697386',
                      fontWeight: activeTab === 'details' ? '600' : '500',
                      backgroundColor: 'transparent',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <i className="bi bi-file-text me-2"></i>
                    <span className="d-none d-sm-inline">Details</span>
                    <span className="d-sm-none">Details</span>
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link border-0 ${activeTab === 'images' ? 'active' : ''}`}
                    onClick={() => setActiveTab('images')}
                    type="button"
                    style={{
                      padding: '0.75rem 1rem',
                      borderBottom: activeTab === 'images' ? '2px solid #911311' : '2px solid transparent',
                      color: activeTab === 'images' ? '#911311' : '#697386',
                      fontWeight: activeTab === 'images' ? '600' : '500',
                      backgroundColor: 'transparent',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <i className="bi bi-images me-2"></i>
                    <span className="d-none d-sm-inline">Images ({images.length})</span>
                    <span className="d-sm-none">Images</span>
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link border-0 ${activeTab === 'emailLogs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('emailLogs')}
                    type="button"
                    style={{
                      padding: '0.75rem 1rem',
                      borderBottom: activeTab === 'emailLogs' ? '2px solid #911311' : '2px solid transparent',
                      color: activeTab === 'emailLogs' ? '#911311' : '#697386',
                      fontWeight: activeTab === 'emailLogs' ? '600' : '500',
                      backgroundColor: 'transparent',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <i className="bi bi-envelope-check me-2"></i>
                    <span className="d-none d-sm-inline">Email Logs ({emailLogs.length})</span>
                    <span className="d-sm-none">Email Logs</span>
                  </button>
                </li>
              </ul>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="modal-body" style={{ 
              padding: '1.5rem', 
              overflowY: 'auto',
              flex: 1,
              minHeight: 0
            }}>
              {error && (
                <div className="alert alert-danger" style={{ 
                  borderRadius: '12px', 
                  border: '1px solid #fecaca',
                  backgroundColor: '#fef2f2',
                  color: '#dc2626'
                }}>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}
              
              {success && (
                <div className="alert alert-success" style={{ 
                  borderRadius: '12px', 
                  border: '1px solid #86efac',
                  backgroundColor: '#f0fdf4',
                  color: '#16a34a',
                  marginBottom: '1rem'
                }}>
                  <i className="bi bi-check-circle me-2"></i>
                  {success}
                </div>
              )}
              
              {/* Details Tab */}
              {activeTab === 'details' && (
                <>
                  {!editMode ? (
                    <div className="row g-3">
                      {/* Basic Information Card */}
                      <div className="col-12">
                        <div className="card" style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          border: '1px solid #e5e9f0',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}>
                          <div className="card-header" style={{
                            backgroundColor: '#f8fafc',
                            borderBottom: '1px solid #e5e9f0',
                            padding: '1rem 1.25rem',
                            borderRadius: '12px 12px 0 0'
                          }}>
                            <h6 className="mb-0" style={{ 
                              fontSize: '1rem', 
                              fontWeight: '600', 
                              color: '#1a1f36' 
                            }}>
                              <i className="bi bi-info-circle me-2"></i>
                              Basic Information
                            </h6>
                          </div>
                          <div className="card-body" style={{ padding: '1.25rem' }}>
                            <div className="row g-3">
                              <div className="col-12 col-lg-6">
                                <div style={{ marginBottom: '1rem' }}>
                                  <label style={{ 
                                    fontSize: '0.875rem', 
                                    fontWeight: '600', 
                                    color: '#697386',
                                    marginBottom: '0.5rem',
                                    display: 'block'
                                  }}>
                                    Customer
                                  </label>
                                  <div style={{ 
                                    fontSize: '1rem', 
                                    color: '#1a1f36',
                                    fontWeight: '500',
                                    wordBreak: 'break-word'
                                  }}>
                                    {ticket.Customer?.companyName || ticket.customerId}
                                  </div>
                                </div>
                              </div>
                              <div className="col-12 col-lg-6">
                                <div style={{ marginBottom: '1rem' }}>
                                  <label style={{ 
                                    fontSize: '0.875rem', 
                                    fontWeight: '600', 
                                    color: '#697386',
                                    marginBottom: '0.5rem',
                                    display: 'block'
                                  }}>
                                    Job
                                  </label>
                                  <div style={{ 
                                    fontSize: '1rem', 
                                    color: '#1a1f36',
                                    fontWeight: '500',
                                    wordBreak: 'break-word'
                                  }}>
                                    {ticket.Job?.title || ticket.jobId}
                                  </div>
                                </div>
                              </div>

                              {!hideMoney && (
                                <div className="col-12">
                                  {jobBudgetLoading ? (
                                    <div className="alert alert-info mb-0">
                                      <strong>Job Budget</strong> <span className="text-muted">Loading...</span>
                                    </div>
                                  ) : jobBudgetSummary?.hasBudget ? (
                                    <div className="alert alert-info mb-0">
                                      <div><strong>Job Budget:</strong> {formatCurrency(Number(jobBudgetSummary.budget || 0))}</div>
                                      <div><strong>Used to Date:</strong> {formatCurrency(Number(jobBudgetSummary.usedToDate || 0))}</div>
                                      <div><strong>Remaining:</strong> {formatCurrency(Number(jobBudgetSummary.remaining || 0))}</div>
                                    </div>
                                  ) : null}
                                </div>
                              )}

                              <div className="col-12 col-lg-6">
                                <div style={{ marginBottom: '1rem' }}>
                                  <label style={{ 
                                    fontSize: '0.875rem', 
                                    fontWeight: '600', 
                                    color: '#697386',
                                    marginBottom: '0.5rem',
                                    display: 'block'
                                  }}>
                                    Work Date
                                  </label>
                                  <div style={{ 
                                    fontSize: '1rem', 
                                    color: '#1a1f36',
                                    fontWeight: '500'
                                  }}>
                                    {ticket.workDate}
                                  </div>
                                </div>
                              </div>
                              {((fullTicketData || ticket).workStartDate || (fullTicketData || ticket).workEndDate) && (
                                <div className="col-12 col-lg-6">
                                  <div style={{ marginBottom: '1rem' }}>
                                    <label style={{
                                      fontSize: '0.875rem',
                                      fontWeight: '600',
                                      color: '#697386',
                                      marginBottom: '0.5rem',
                                      display: 'block'
                                    }}>
                                      Planned work (schedule)
                                    </label>
                                    <div style={{
                                      fontSize: '1rem',
                                      color: '#1a1f36',
                                      fontWeight: '500'
                                    }}>
                                      {(fullTicketData || ticket).workStartDate || '—'}
                                      {' → '}
                                      {(fullTicketData || ticket).workEndDate || '—'}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {((fullTicketData || ticket).createdByName) && (
                                <div className="col-12 col-lg-6">
                                  <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ 
                                      fontSize: '0.875rem', 
                                      fontWeight: '600', 
                                      color: '#697386',
                                      marginBottom: '0.5rem',
                                      display: 'block'
                                    }}>
                                      Creator
                                    </label>
                                    <div style={{ 
                                      fontSize: '1rem', 
                                      color: '#1a1f36',
                                      fontWeight: '500',
                                      wordBreak: 'break-word'
                                    }}>
                                      {(fullTicketData || ticket).createdByName}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {(ticket.description || ticket.notes) && (
                                <>
                                  {ticket.description && (
                                    <div className="col-12 col-lg-6">
                                      <div style={{ marginBottom: '1rem' }}>
                                        <label style={{ 
                                          fontSize: '0.875rem', 
                                          fontWeight: '600', 
                                          color: '#697386',
                                          marginBottom: '0.5rem',
                                          display: 'block'
                                        }}>
                                          Description
                                        </label>
                                        <div style={{ 
                                          fontSize: '1rem', 
                                          color: '#1a1f36',
                                          fontWeight: '400',
                                          lineHeight: '1.5',
                                          wordBreak: 'break-word'
                                        }}>
                                          {ticket.description}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {ticket.notes && (
                                    <div className="col-12 col-lg-6">
                                      <div style={{ marginBottom: '1rem' }}>
                                        <label style={{ 
                                          fontSize: '0.875rem', 
                                          fontWeight: '600', 
                                          color: '#697386',
                                          marginBottom: '0.5rem',
                                          display: 'block'
                                        }}>
                                          Notes
                                        </label>
                                        <div style={{ 
                                          fontSize: '1rem', 
                                          color: '#1a1f36',
                                          fontWeight: '400',
                                          lineHeight: '1.5',
                                          wordBreak: 'break-word'
                                        }}>
                                          {ticket.notes}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Equipment & Hours Card */}
                      <div className="col-12">
                        <div className="card" style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          border: '1px solid #e5e9f0',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}>
                          <div className="card-header" style={{
                            backgroundColor: '#f8fafc',
                            borderBottom: '1px solid #e5e9f0',
                            padding: '1rem 1.25rem',
                            borderRadius: '12px 12px 0 0'
                          }}>
                            <h6 className="mb-0" style={{ 
                              fontSize: '1rem', 
                              fontWeight: '600', 
                              color: '#1a1f36' 
                            }}>
                              <i className="bi bi-gear me-2"></i>
                              Equipment & Hours
                            </h6>
                          </div>
                          <div className="card-body" style={{ padding: '1.25rem' }}>
                            {(() => {
                              const totalGallons = ticket.TicketItems ? ticket.TicketItems.reduce((sum, item) => {
                                const gallons = parseFloat(item.gallonsUsed) || 0;
                                return sum + gallons;
                              }, 0) : 0;
                              return totalGallons > 0 ? (
                                <div className="alert alert-info mb-3 d-flex justify-content-between align-items-center">
                                  <strong>TOTAL GALLONS USED:</strong>
                                  <span className="badge bg-primary fs-6">{totalGallons.toFixed(2)} gallons</span>
                                </div>
                              ) : null;
                            })()}
                            {ticket.TicketItems && ticket.TicketItems.length > 0 ? (
                              <div className="row g-3">
                                {ticket.TicketItems.map((item, idx) => (
                                  <div key={idx} className="col-12">
                                    <div style={{
                                      backgroundColor: '#f8fafc',
                                      borderRadius: '8px',
                                      padding: '1rem',
                                      border: '1px solid #e5e9f0'
                                    }}>
                                      <div className="row g-3">
                                        <div className="col-12 col-md-6 col-lg-3">
                                          <div style={{ marginBottom: '0.5rem' }}>
                                            <label style={{ 
                                              fontSize: '0.75rem', 
                                              fontWeight: '600', 
                                              color: '#697386',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.05em'
                                            }}>
                                              Machine
                                            </label>
                                            <div style={{ 
                                              fontSize: '1rem', 
                                              color: '#1a1f36',
                                              fontWeight: '600',
                                              wordBreak: 'break-word'
                                            }}>
                                              {item.Machine?.name || item.machineId}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                          <div style={{ marginBottom: '0.5rem' }}>
                                            <label style={{ 
                                              fontSize: '0.75rem', 
                                              fontWeight: '600', 
                                              color: '#697386',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.05em'
                                            }}>
                                              Operator
                                            </label>
                                            <div style={{ 
                                              fontSize: '0.875rem', 
                                              color: '#1a1f36',
                                              fontWeight: '500',
                                              wordBreak: 'break-word'
                                            }}>
                                              {item.Employee?.name || 'N/A'}
                                            </div>
                                          </div>
                                        </div>
                                        {item.gallonsUsed && parseFloat(item.gallonsUsed) > 0 && (
                                          <div className="col-12 col-md-6 col-lg-3">
                                            <div style={{ marginBottom: '0.5rem' }}>
                                              <label style={{ 
                                                fontSize: '0.75rem', 
                                                fontWeight: '600', 
                                                color: '#697386',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                              }}>
                                                Gallons Used
                                              </label>
                                              <div style={{ 
                                                fontSize: '0.875rem', 
                                                color: '#1a1f36',
                                                fontWeight: '500',
                                                wordBreak: 'break-word'
                                              }}>
                                                {parseFloat(item.gallonsUsed).toFixed(2)} gallons
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        <div className="col-12 col-md-6 col-lg-3">
                                          <div style={{ marginBottom: '0.5rem' }}>
                                            <label style={{ 
                                              fontSize: '0.75rem', 
                                              fontWeight: '600', 
                                              color: '#697386',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.05em'
                                            }}>
                                              Rate Type
                                            </label>
                                            <div style={{ 
                                              fontSize: '0.875rem', 
                                              color: '#1a1f36',
                                              fontWeight: '500'
                                            }}>
                                              {item.rateType === 'hourly'
                                                ? 'Hourly'
                                                : item.rateType === 'load'
                                                  ? 'Per Load'
                                                  : 'Daily'}
                                              {item.rateType === 'hourly' && item.hoursUsed && (
                                                <span style={{ color: '#697386', marginLeft: '0.5rem' }}>
                                                  ({item.hoursUsed} hrs)
                                                </span>
                                              )}
                                              {item.rateType === 'load' && item.loadsUsed && (
                                                <span style={{ color: '#697386', marginLeft: '0.5rem' }}>
                                                  ({item.loadsUsed} loads)
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        {!hideMoney && (
                                          <div className="col-12 col-md-6 col-lg-3">
                                            <div style={{ marginBottom: '0.5rem' }}>
                                              <label style={{ 
                                                fontSize: '0.75rem', 
                                                fontWeight: '600', 
                                                color: '#697386',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                              }}>
                                                Amount
                                              </label>
                                              <div style={{ 
                                                fontSize: '0.875rem', 
                                                color: '#1a1f36',
                                                fontWeight: '600'
                                              }}>
                                                ${item.amount || '0.00'}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      {(item.description || item.notes) && (
                                        <div className="row mt-2">
                                          {item.description && (
                                            <div className="col-12 col-lg-6">
                                              <div style={{ 
                                                fontSize: '0.875rem', 
                                                color: '#697386',
                                                fontStyle: 'italic',
                                                wordBreak: 'break-word'
                                              }}>
                                                <strong>Description:</strong> {item.description}
                                              </div>
                                            </div>
                                          )}
                                          {item.notes && (
                                            <div className="col-12 col-lg-6">
                                              <div style={{ 
                                                fontSize: '0.875rem', 
                                                color: '#697386',
                                                fontStyle: 'italic',
                                                wordBreak: 'break-word'
                                              }}>
                                                <strong>Notes:</strong> {item.notes}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '2rem',
                                color: '#697386',
                                fontSize: '0.875rem'
                              }}>
                                <i className="bi bi-inbox" style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}></i>
                                No equipment items found
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      {/* Basic Information */}
                      <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Customer *</label>
                          <select className="form-select" name="customerId" value={formData.customerId} onChange={handleFormChange} required>
                            <option value="">Select Customer</option>
                            {[...customers].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '', undefined, { sensitivity: 'base' })).map(customer => (
                              <option key={customer.id} value={customer.id}>{customer.companyName}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Job Name *</label>
                          <select className="form-select" name="jobId" value={formData.jobId} onChange={handleFormChange} required disabled={!formData.customerId}>
                            <option value="">Select Job Name</option>
                            {formData.customerId && jobs.filter(job => job.customerId === parseInt(formData.customerId)).map(job => (
                              <option key={job.id} value={job.id}>{job.title}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Work Date *</label>
                          <input type="date" className="form-control" name="workDate" value={formData.workDate} onChange={handleFormChange} required />
                          <div className="form-text">Day the ticket was submitted / work logged.</div>
                        </div>
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Status *</label>
                          <select className="form-select" name="status" value={formData.status || 'Pending'} onChange={handleFormChange} required>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Invoiced">Invoiced</option>
                            <option value="Paid">Paid</option>
                            <option value="Rejected">Rejected</option>
                          </select>
                        </div>
                      </div>
                      <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Planned work start (optional)</label>
                          <input
                            type="date"
                            className="form-control"
                            name="workStartDate"
                            value={formData.workStartDate || ''}
                            onChange={handleFormChange}
                          />
                        </div>
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Planned work end (optional)</label>
                          <input
                            type="date"
                            className="form-control"
                            name="workEndDate"
                            value={formData.workEndDate || ''}
                            onChange={handleFormChange}
                          />
                        </div>
                      </div>
                      <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Description</label>
                          <textarea className="form-control" name="description" value={formData.description} onChange={handleFormChange} rows="2" placeholder="Brief description of work performed..." style={{ minHeight: '60px', maxHeight: '120px' }} />
                        </div>
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Notes</label>
                          <textarea className="form-control" name="notes" value={formData.notes} onChange={handleFormChange} rows="2" placeholder="Additional notes..." style={{ minHeight: '60px', maxHeight: '120px' }} />
                        </div>
                      </div>

                      {!hideMoney && jobBudgetLoading ? (
                        <div className="alert alert-info mb-3 d-flex justify-content-between align-items-center">
                          <strong>Job Budget</strong>
                          <span className="text-muted">Loading...</span>
                        </div>
                      ) : !hideMoney && jobBudgetSummary?.hasBudget ? (
                        <div className="alert alert-info mb-3">
                          <div><strong>Job Budget:</strong> {formatCurrency(Number(jobBudgetSummary.budget || 0))}</div>
                          <div><strong>Used to Date:</strong> {formatCurrency(Number(jobBudgetSummary.usedToDate || 0))}</div>
                          <div><strong>Remaining:</strong> {formatCurrency(Number(jobBudgetSummary.remaining || 0))}</div>
                        </div>
                      ) : null}
                      
                      {/* Total Gallons Display */}
                      {(() => {
                        const totalGallons = formData.ticketItems.reduce((sum, item) => {
                          const gallons = parseFloat(item.gallonsUsed) || 0;
                          return sum + gallons;
                        }, 0);
                        return totalGallons > 0 ? (
                          <div className="alert alert-info mb-3 d-flex justify-content-between align-items-center">
                            <strong>TOTAL GALLONS USED:</strong>
                            <span className="badge bg-primary fs-6">{totalGallons.toFixed(2)} gallons</span>
                          </div>
                        ) : null;
                      })()}

                      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <h6 className="mb-0">Equipment & Hours</h6>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={addTicketItem}>
                          <i className="bi bi-plus-circle me-1"></i> Add Machine
                        </button>
                      </div>
                      {formData.ticketItems.map((item, index) => (
                        <div key={index} className="card mb-2 ticket-item-card">
                          <div className="card-body">
                            <div className="row g-2">
                              <div className="col-12 col-md-6 col-lg-3">
                                <label className="form-label">Machine *</label>
                                <select className="form-select" value={item.machineId} onChange={e => handleTicketItemChange(index, 'machineId', e.target.value)} required>
                                  <option value="">Select Machine</option>
                                  {machines
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map(machine => (
                                      <option key={machine.id} value={machine.id}>
                                        {hideMoney
                                          ? formatMachineOptionLabelNoFinance(machine, String(formData.workDate || '').slice(0, 10))
                                          : formatMachineOptionLabel(machine, String(formData.workDate || '').slice(0, 10))}
                                      </option>
                                    ))}
                                </select>
                              </div>
                              
                              <div className="col-12 col-md-6 col-lg-3">
                                <label className="form-label">Operator *</label>
                                <select className="form-select" value={item.employeeId} onChange={e => handleTicketItemChange(index, 'employeeId', e.target.value)} required>
                                  <option value="">Select Operator</option>
                                  {employees.map(employee => (
                                    <option key={employee.id} value={employee.id}>
                                      {employee.name} - {employee.position}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label">Rate Type *</label>
                                <select className="form-select" value={item.rateType} onChange={e => handleTicketItemChange(index, 'rateType', e.target.value)} required>
                                  <option value="hourly">Hourly</option>
                                  <option value="daily">Daily</option>
                                  <option value="load">Per Load</option>
                                </select>
                              </div>
                              {item.rateType === 'hourly' && (
                                <div className="col-12 col-md-6 col-lg-2">
                                  <label className="form-label">Hours *</label>
                                  <input type="number" className="form-control" value={item.hoursUsed} onChange={e => handleTicketItemChange(index, 'hoursUsed', e.target.value)} step="0.5" min="0" required />
                                </div>
                              )}
                              {item.rateType === 'load' && (
                                <div className="col-12 col-md-6 col-lg-2">
                                  <label className="form-label">Loads Hauled *</label>
                                  <input type="number" className="form-control" value={item.loadsUsed} onChange={e => handleTicketItemChange(index, 'loadsUsed', e.target.value)} step="1" min="0" required />
                                </div>
                              )}
                              <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label">Gallons Used</label>
                                <input type="number" className="form-control" value={item.gallonsUsed} onChange={e => handleTicketItemChange(index, 'gallonsUsed', e.target.value)} step="0.01" min="0" placeholder="0.00" />
                              </div>
                              
                              <div className="col-12 col-md-6 col-lg-1">
                                <button type="button" className="btn btn-outline-danger btn-sm w-100" onClick={() => removeTicketItem(index)} disabled={formData.ticketItems.length === 1}>
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </div>
                            <div className="row g-2 mt-2">
                              <div className="col-12 col-lg-6">
                                <label className="form-label">Description</label>
                                <input type="text" className="form-control" value={item.description} onChange={e => handleTicketItemChange(index, 'description', e.target.value)} placeholder="Work description for this machine..." />
                              </div>
                              <div className="col-12 col-lg-6">
                                <label className="form-label">Notes</label>
                                <input type="text" className="form-control" value={item.notes} onChange={e => handleTicketItemChange(index, 'notes', e.target.value)} placeholder="Additional notes..." />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={loading}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? (<><span className="spinner-border spinner-border-sm me-2" role="status"></span>Saving...</>) : ('Save Changes')}</button>
                      </div>
                    </form>
                  )}
                </>
              )}

              {/* Images Tab */}
              {activeTab === 'images' && (
                <div className="row g-3">
                  <div className="col-12">
                    <div className="card" style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '12px',
                      border: '1px solid #e5e9f0',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                      <div className="card-header" style={{
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e5e9f0',
                        padding: '1rem 1.25rem',
                        borderRadius: '12px 12px 0 0'
                      }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <h6 className="mb-0" style={{ 
                            fontSize: '1rem', 
                            fontWeight: '600', 
                            color: '#1a1f36' 
                          }}>
                            <i className="bi bi-images me-2"></i>
                            Ticket Images
                          </h6>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowImageUpload(true)}
                            style={{
                              backgroundColor: '#911311',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '0.5rem 1rem',
                              fontWeight: '600',
                              fontSize: '0.875rem'
                            }}
                          >
                            <i className="bi bi-plus-circle me-2"></i>
                            <span className="d-none d-sm-inline">Upload Images</span>
                            <span className="d-sm-none">Upload</span>
                          </button>
                        </div>
                      </div>
                      <div className="card-body" style={{ padding: '1.25rem' }}>
                        <ImageGallery
                          images={images}
                          onImageDeleted={handleImageDeleted}
                          onImageUpdated={handleImageUpdated}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Logs Tab */}
              {activeTab === 'emailLogs' && (
                <div className="row g-3">
                  <div className="col-12">
                    <div className="card" style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '12px',
                      border: '1px solid #e5e9f0',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                      <div className="card-header" style={{
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e5e9f0',
                        padding: '1rem 1.25rem',
                        borderRadius: '12px 12px 0 0'
                      }}>
                        <h6 className="mb-0" style={{ 
                          fontSize: '1rem', 
                          fontWeight: '600', 
                          color: '#1a1f36' 
                        }}>
                          <i className="bi bi-envelope-check me-2"></i>
                          Email History
                        </h6>
                      </div>
                      <div className="card-body" style={{ padding: '1.25rem' }}>
                        {loadingEmailLogs ? (
                          <div className="text-center" style={{ padding: '2rem' }}>
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        ) : emailLogs.length === 0 ? (
                          <div className="text-center" style={{ padding: '3rem 1rem', color: '#697386' }}>
                            <i className="bi bi-envelope-x" style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}></i>
                            <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem' }}>No email history</p>
                            <p style={{ fontSize: '0.875rem', opacity: 0.8 }}>No emails have been sent for this ticket yet.</p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-hover" style={{ marginBottom: 0 }}>
                              <thead style={{ backgroundColor: '#f8fafc' }}>
                                <tr>
                                  <th style={{ borderBottom: '2px solid #e5e9f0', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#1a1f36' }}>Sent Date/Time</th>
                                  <th style={{ borderBottom: '2px solid #e5e9f0', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#1a1f36' }}>Recipient</th>
                                  <th style={{ borderBottom: '2px solid #e5e9f0', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#1a1f36' }}>Status</th>
                                  <th style={{ borderBottom: '2px solid #e5e9f0', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#1a1f36' }}>Subject</th>
                                  <th style={{ borderBottom: '2px solid #e5e9f0', padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#1a1f36' }}>Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {emailLogs.map((log) => (
                                  <tr key={log.id} style={{ borderBottom: '1px solid #e5e9f0' }}>
                                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#1a1f36' }}>
                                      {log.sentAt ? formatDateTimeForDisplay(log.sentAt) : formatDateTimeForDisplay(log.createdAt)}
                                    </td>
                                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#1a1f36' }}>
                                      {log.recipientEmail}
                                    </td>
                                    <td style={{ padding: '0.75rem' }}>
                                      {log.receivedAt ? (
                                        <>
                                          <span
                                            className="badge"
                                            style={{
                                              backgroundColor: '#2563eb',
                                              color: '#ffffff',
                                              padding: '0.375rem 0.75rem',
                                              borderRadius: '6px',
                                              fontSize: '0.75rem',
                                              fontWeight: '600'
                                            }}
                                          >
                                            <i className="bi bi-check2-all me-1"></i>
                                            Received
                                          </span>
                                          <div style={{ fontSize: '0.75rem', color: '#697386', marginTop: '0.25rem', whiteSpace: 'nowrap' }}>
                                            {formatDateTimeForDisplay(log.receivedAt)}
                                          </div>
                                        </>
                                      ) : (
                                      <span
                                        className="badge"
                                        style={{
                                          backgroundColor: log.status === 'sent' ? '#10b981' : log.status === 'failed' ? '#ef4444' : '#f59e0b',
                                          color: '#ffffff',
                                          padding: '0.375rem 0.75rem',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem',
                                          fontWeight: '600',
                                          textTransform: 'capitalize'
                                        }}
                                      >
                                        {log.status === 'sent' && <i className="bi bi-check-circle me-1"></i>}
                                        {log.status === 'failed' && <i className="bi bi-x-circle me-1"></i>}
                                        {log.status === 'pending' && <i className="bi bi-clock me-1"></i>}
                                        {log.status}
                                      </span>
                                      )}
                                    </td>
                                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#1a1f36' }}>
                                      {log.subject || 'N/A'}
                                    </td>
                                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: log.errorMessage ? '#ef4444' : '#697386' }}>
                                      {log.errorMessage || '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {!editMode && (
              <div className="modal-footer" style={{ 
                borderTop: '1px solid #e5e9f0',
                padding: '1rem 1.5rem',
                backgroundColor: '#f8fafc',
                flexShrink: 0
              }}>
                <div className="d-flex gap-2 w-100 flex-wrap">
                  <button type="button" className="btn btn-secondary flex-fill" onClick={onClose} style={{
                    backgroundColor: '#ffffff',
                    color: '#1a1f36',
                    border: '1px solid #e5e9f0',
                    borderRadius: '8px',
                    padding: '0.625rem 1.25rem',
                    fontWeight: '600',
                    minHeight: '44px'
                  }}>
                    Close
                  </button>
                  {activeTab === 'details' && (
                    <button type="button" className="btn btn-primary flex-fill" onClick={handleEdit} style={{
                      backgroundColor: '#911311',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.625rem 1.25rem',
                      fontWeight: '600',
                      minHeight: '44px'
                    }}>
                      Edit
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      {showImageUpload && (
        <ImageUpload
          ticketId={ticket.id}
          onImagesUploaded={handleImagesUploaded}
          onClose={() => setShowImageUpload(false)}
        />
      )}

       {/* Email Preview Confirmation Modal */}
       {showEmailPreviewModal && emailPreview && (
         <div className="modal fade show" style={{ display: 'block', zIndex: 1600 }} tabIndex="-1" role="dialog">
           <div className="modal-dialog modal-dialog-scrollable" role="document" style={{ maxWidth: '90vw', width: '90vw' }}>
            <div className="modal-content" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #e5e9f0', padding: '1.5rem' }}>
                <h5 className="modal-title" style={{ fontWeight: '600', fontSize: '1.25rem', color: '#1a1f36' }}>
                  <i className="bi bi-envelope-check me-2"></i>
                  Confirm Email Send
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEmailPreviewModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{ padding: '1.5rem' }}>
                {/* Email Details */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h6 style={{ fontWeight: '600', marginBottom: '1rem', color: '#1a1f36' }}>Email Details</h6>
                  <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '8px' }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>From:</strong>
                      <span style={{ color: '#1a1f36' }}>{emailPreview.from}</span>
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>To:</strong>
                      <span style={{ color: '#1a1f36' }}>{emailPreview.to}</span>
                    </div>
                    {emailPreview.replyTo && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <strong style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Reply To:</strong>
                        <span style={{ color: '#1a1f36' }}>{emailPreview.replyTo}</span>
                      </div>
                    )}
                    <div>
                      <strong style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Subject:</strong>
                      <span style={{ color: '#1a1f36', fontWeight: '500' }}>{emailPreview.subject}</span>
                    </div>
                  </div>
                </div>

                 {/* Email Body Preview */}
                 <div>
                   <h6 style={{ fontWeight: '600', marginBottom: '1rem', color: '#1a1f36' }}>Email Preview</h6>
                   <div
                     style={{
                       border: '1px solid #e5e9f0',
                       borderRadius: '8px',
                       padding: '1rem',
                       backgroundColor: '#ffffff',
                       maxHeight: '600px',
                       overflowY: 'auto',
                       width: '100%'
                     }}
                   >
                     <iframe
                       srcDoc={emailPreview.html}
                       style={{
                         width: '100%',
                         border: 'none',
                         minHeight: '500px',
                         height: 'auto'
                       }}
                       title="Email Preview"
                     />
                   </div>
                 </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e9f0', padding: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEmailPreviewModal(false)}
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#1a1f36',
                    border: '1px solid #e5e9f0',
                    borderRadius: '8px',
                    padding: '0.625rem 1.25rem',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  style={{
                    backgroundColor: '#911311',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.625rem 1.25rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {sendingEmail ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-envelope-send"></i>
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ opacity: 0.5 }} onClick={() => setShowEmailPreviewModal(false)}></div>
        </div>
      )}
    </>
  );
};

export default ViewEditTicketModal; 